// src/OEMFormatManager.jsx - Manage OEM format rules (CRUD)
import React, { useEffect, useState } from 'react';
import axios from 'axios';

export default function OEMFormatManager({ permissions, theme }) {
  const [rules, setRules] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ Manufacturer: '', Matcher: '', FormatTemplate: '', Priority: 999 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/oem-format-rules');
      setRules(data.sort((a, b) => a.Manufacturer.localeCompare(b.Manufacturer) || a.Priority - b.Priority));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`/api/oem-format-rules/${editingId}`, formData);
      } else {
        await axios.post('/api/oem-format-rules', formData);
      }
      fetchRules();
      setEditingId(null);
      setFormData({ Manufacturer: '', Matcher: '', FormatTemplate: '', Priority: 999 });
    } catch (err) {
      setError(err?.response?.data?.error || 'Save failed');
    }
  };

  const handleEdit = (rule) => {
    setEditingId(rule.RuleKey);
    setFormData({
      Manufacturer: rule.Manufacturer,
      Matcher: rule.Matcher || '',
      FormatTemplate: rule.FormatTemplate,
      Priority: rule.Priority
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await axios.delete(`/api/oem-format-rules/${id}`);
      fetchRules();
    } catch (err) {
      setError(err?.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <h2>OEM Format Rules Manager</h2>
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>Manufacturer</th>
              <th>Matcher (Regex)</th>
              <th>Format Template</th>
              <th>Priority</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.RuleKey}>
                <td>{r.Manufacturer}</td>
                <td>{r.Matcher || 'Default'}</td>
                <td>{r.FormatTemplate}</td>
                <td>{r.Priority}</td>
                <td>
                  <button onClick={() => handleEdit(r)}>Edit</button>
                  <button onClick={() => handleDelete(r.RuleKey)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>{editingId ? 'Edit' : 'Add'} Rule</h3>
      <form onSubmit={handleSubmit}>
        <input
          name="Manufacturer"
          placeholder="Manufacturer (e.g., Honda)"
          value={formData.Manufacturer}
          onChange={handleChange}
          required
        />
        <input
          name="Matcher"
          placeholder="Matcher Regex (optional, e.g., ^95701.*)"
          value={formData.Matcher}
          onChange={handleChange}
        />
        <input
          name="FormatTemplate"
          placeholder="Format Template (e.g., XXXXX-XXX-XXX)"
          value={formData.FormatTemplate}
          onChange={handleChange}
          required
        />
        <input
          type="number"
          name="Priority"
          placeholder="Priority (lower = higher pref)"
          value={formData.Priority}
          onChange={handleChange}
          required
        />
        <button type="submit">Save</button>
        {editingId && <button onClick={() => { setEditingId(null); setFormData({ Manufacturer: '', Matcher: '', FormatTemplate: '', Priority: 999 }); }}>Cancel</button>}
      </form>
    </div>
  );
}